import { fetchToken, messaging } from '@/configs/firebase'
import { addNewTokenDevice } from '../services'
import useAuthStore from '@/stores/useAuthStore'
import useNotificationStore from '@/stores/useNotificationStore'
import { onMessage, Unsubscribe } from 'firebase/messaging'
import { useEffect, useRef } from 'react'

async function getNotificationPermissionAndToken() {
    // Step 1: Check if Notifications are supported in the browser.
    if (!('Notification' in window)) {
        return null
    }

    // Step 2: Check if permission is already granted.
    if (Notification.permission === 'granted') {
        return await fetchToken()
    }

    // Step 3: If permission is not denied, request permission from the user.
    if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
            return await fetchToken()
        }
    }

    return null
}

const useFcmToken = () => {
    const {
        notificationPermissionStatus,
        setNotificationPermissionStatus,
        setToken,
        token,
    } = useNotificationStore()
    const retryLoadToken = useRef(0) // Ref to keep track of retry attempts.
    const isLoading = useRef(false) // Ref to keep track if a token fetch is currently in progress.
    const { employee } = useAuthStore()

    const loadToken = async () => {
        // Step 4: Prevent multiple fetches if already fetched or in progress.
        if (isLoading.current) return

        isLoading.current = true // Mark loading as in progress.
        const token = await getNotificationPermissionAndToken()

        // Step 5: Handle the case where permission is denied.
        if (Notification.permission === 'denied') {
            setNotificationPermissionStatus('denied')
            isLoading.current = false
            return
        }

        // Step 6: Retry fetching the token if necessary. (up to 3 times)
        // This step is typical initially as the service worker may not be ready/installed yet.
        if (!token) {
            if (retryLoadToken.current >= 3) {
                alert('Unable to load token, refresh the browser')
                isLoading.current = false
                return
            }

            retryLoadToken.current += 1
            console.error(
                'An error occurred while retrieving token. Retrying...'
            )
            isLoading.current = false
            await loadToken()
            return
        }

        // Step 7: Set the fetched token and mark as fetched.
        setNotificationPermissionStatus(Notification.permission)
        setToken(token)
        isLoading.current = false
    }

    useEffect(() => {
        if (!employee?.employee_id) return
        // Step 8: Initialize token loading when the component mounts.
        if ('Notification' in window) {
            loadToken()
        }
    }, [employee, notificationPermissionStatus])

    useEffect(() => {
        const setupListener = async () => {
            if (!token) return // Exit if no token is available.

            const m = await messaging()
            if (!m) return

            // Step 9: Register a listener for incoming FCM messages.
            const unsubscribe = onMessage(m, (payload) => {
                if (Notification.permission !== 'granted') return

                const link = payload.fcmOptions?.link || payload.data?.link

                // --------------------------------------------
                // Disable this if you only want toast notifications.
                const n = new Notification(
                    payload.notification?.title || 'New message',
                    {
                        body:
                            payload.notification?.body ||
                            'This is a new message',
                        data: link ? { url: link } : undefined,
                        icon: 'https://partner.vnetwork.vn/assets/icon.png',
                    }
                )

                // Step 10: Handle notification click event to navigate to a link if present.
                n.onclick = (event) => {
                    event.preventDefault()
                    const link = (event.target as any)?.data?.url
                    if (link) {
                        window.open(link)
                    }
                }
                // --------------------------------------------
            })

            return unsubscribe
        }

        let unsubscribe: Unsubscribe | null = null

        setupListener().then((unsub) => {
            if (unsub) {
                unsubscribe = unsub
            }
        })

        // Step 11: Cleanup the listener when the component unmounts.
        return () => unsubscribe?.()
    }, [token])

    useEffect(() => {
        if (
            notificationPermissionStatus !== 'granted' ||
            !employee?.employee_id ||
            !token
        )
            return

        addNewTokenDevice({
            employee_id: employee?.employee_id,
            token_device: token,
        })
    }, [notificationPermissionStatus, token, employee])

    return { token, notificationPermissionStatus } // Return the token and permission status.
}

export default useFcmToken
